import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ListChecks, CalendarClock, Info, Users, Search, X } from "lucide-react";
import { TIPOS_MODALIDAD, VARIANTE_NIAGARA, ESTADOS_RESERVA, paxResumen, idiomaAbbr } from "@/lib/constants";
import { formatFechaCorta } from "@/lib/agenda";
import { nombreTourDisplay } from "@/lib/tourDisplay";

const money = (v, m) => (v == null ? "—" : `${m === "USD" || !m ? "$" : ""}${Number(v).toFixed(2)}${m && m !== "USD" ? " " + m : ""}`);

function ReprogramarDialog({ reserva, onDone }) {
  const [open, setOpen] = useState(false);
  const [fecha, setFecha] = useState(reserva.fecha || "");
  const [hora, setHora] = useState(reserva.hora || "");
  const [saving, setSaving] = useState(false);

  const enviar = async () => {
    if (!fecha || !hora) return toast.error("Indicá nueva fecha y horario.");
    setSaving(true);
    try {
      const { data } = await api.post(`/portal/reservas/${reserva.id}/reprogramar`, { fecha, hora });
      if (data.resultado === "reprogramada") toast.success("Fecha cambiada. Cupo actualizado.");
      else toast.info("Solicitud enviada a Tours NY para aprobación (menos de 24 h).");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`reprogramar-btn-${reserva.id}`} className="gap-1.5">
        <CalendarClock className="h-3.5 w-3.5" /> Cambiar fecha
      </Button>
      <DialogContent data-testid="reprogramar-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Cambiar fecha / horario</DialogTitle>
          <DialogDescription>
            Con 24 h o más de anticipación el cambio es automático. Con menos, queda pendiente de aprobación de Tours NY.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Nueva fecha</Label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="reprogramar-fecha" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Nuevo horario</Label>
            <Input type="time" value={hora} onChange={(e) => setHora(e.target.value)} data-testid="reprogramar-hora" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={enviar} disabled={saving} data-testid="reprogramar-confirmar">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditarPaxDialog({ reserva, onDone }) {
  const [open, setOpen] = useState(false);
  const [adultos, setAdultos] = useState(reserva.adultos || 1);
  const [ninos, setNinos] = useState(reserva.ninos || 0);
  const [infantes, setInfantes] = useState(reserva.infantes || 0);
  const [saving, setSaving] = useState(false);

  const abrir = () => {
    setAdultos(reserva.adultos || 1);
    setNinos(reserva.ninos || 0);
    setInfantes(reserva.infantes || 0);
    setOpen(true);
  };

  const enviar = async () => {
    if (Number(adultos) < 1) return toast.error("Debe haber al menos 1 adulto.");
    setSaving(true);
    try {
      const { data } = await api.post(`/portal/reservas/${reserva.id}/editar-pax`, {
        adultos: Number(adultos), ninos: Number(ninos), infantes: Number(infantes),
      });
      if (data.resultado === "aplicado") toast.success("Pasajeros actualizados. Cupo recalculado.");
      else toast.info("Solicitud enviada a Tours NY para aprobación (menos de 24 h).");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" size="sm" onClick={abrir} data-testid={`editar-pax-btn-${reserva.id}`} className="gap-1.5">
        <Users className="h-3.5 w-3.5" /> Pasajeros
      </Button>
      <DialogContent data-testid="editar-pax-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Editar cantidad de pasajeros</DialogTitle>
          <DialogDescription>
            Con 24 h o más de anticipación el cambio es automático (si hay cupo). Con menos, queda pendiente de aprobación de Tours NY.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Adultos</Label>
            <Input type="number" min={1} value={adultos} onChange={(e) => setAdultos(e.target.value)} data-testid="editar-pax-adultos" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Niños (4-17)</Label>
            <Input type="number" min={0} value={ninos} onChange={(e) => setNinos(e.target.value)} data-testid="editar-pax-ninos" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Infantes (0-3)</Label>
            <Input type="number" min={0} value={infantes} onChange={(e) => setInfantes(e.target.value)} data-testid="editar-pax-infantes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={enviar} disabled={saving} data-testid="editar-pax-confirmar">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PortalReservas() {
  const [reservas, setReservas] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tourFilter, setTourFilter] = useState("todos");
  const [estadoFilter, setEstadoFilter] = useState("todos");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (tourFilter !== "todos") params.tour_id = tourFilter;
      if (estadoFilter !== "todos") params.estado = estadoFilter;
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const { data } = await api.get("/portal/reservas", { params });
      setReservas(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q, tourFilter, estadoFilter, desde, hasta]);

  useEffect(() => {
    api.get("/portal/tours").then(({ data }) => setTours(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const hayFiltros = q || tourFilter !== "todos" || estadoFilter !== "todos" || desde || hasta;
  const limpiarFiltros = () => {
    setQ("");
    setTourFilter("todos");
    setEstadoFilter("todos");
    setDesde("");
    setHasta("");
  };

  const cancelar = async (r) => {
    try {
      const { data } = await api.post(`/portal/reservas/${r.id}/cancelar`);
      if (data.resultado === "cancelada") toast.success("Reserva cancelada. Cupo liberado.");
      else toast.info("Solicitud de cancelación enviada a Tours NY para aprobación (menos de 24 h).");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const activas = (r) => !["cancelada", "rechazada"].includes(r.estado);

  return (
    <div className="space-y-5" data-testid="portal-reservas">
      <div>
        <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
          <ListChecks className="h-6 w-6 text-indigo-600" /> Mis reservas
        </h2>
        <p className="text-sm text-slate-500 mt-1">Reservas cargadas por tu agencia. Cancelá, cambiá la fecha o editá los pasajeros según la antelación.</p>
      </div>

      <div className="flex items-start gap-2 rounded-md bg-indigo-50 border border-indigo-100 px-3 py-2 text-[13px] text-indigo-800">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <span>Con <strong>24 h o más</strong> de anticipación, cancelar/cambiar es automático. Con <strong>menos de 24 h</strong>, la solicitud queda pendiente de aprobación de Tours NY.</span>
      </div>

      <Card className="p-4" data-testid="portal-reservas-filtros">
        <div className="grid gap-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_minmax(0,1fr)_auto_auto]">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Buscar</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Localizador, pasajero, email o tour…"
                className="pl-8"
                data-testid="portal-reservas-buscador"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Tour</Label>
            <Select value={tourFilter} onValueChange={setTourFilter}>
              <SelectTrigger data-testid="portal-reservas-tour-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los tours</SelectItem>
                {tours.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Estado</Label>
            <Select value={estadoFilter} onValueChange={setEstadoFilter}>
              <SelectTrigger data-testid="portal-reservas-estado-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {Object.entries(ESTADOS_RESERVA).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Desde</Label>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="portal-reservas-desde" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Hasta</Label>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="portal-reservas-hasta" />
          </div>
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-slate-500" data-testid="portal-reservas-count">
            {loading ? "Buscando…" : `${reservas.length} reserva(s)`}
          </span>
          {hayFiltros && (
            <Button variant="ghost" size="sm" onClick={limpiarFiltros} className="gap-1.5 text-slate-600" data-testid="portal-reservas-limpiar">
              <X className="h-3.5 w-3.5" /> Limpiar filtros
            </Button>
          )}
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["Código", "Cliente", "Tour", "Fecha / Hora", "Modalidad", "Pax", "Valor total", "Saldo pendiente", "Estado", "Acciones"].map((h, i) => (
                  <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="h-32 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 inline" /></TableCell></TableRow>
              ) : reservas.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="h-32 text-center text-slate-400 text-sm" data-testid="reservas-empty">{hayFiltros ? "No hay reservas que coincidan con la búsqueda." : "Todavía no cargaste reservas."}</TableCell></TableRow>
              ) : (
                reservas.map((r) => {
                  const est = ESTADOS_RESERVA[r.estado] || { label: r.estado, cls: "bg-slate-100 text-slate-700 border-slate-200" };
                  return (
                    <TableRow key={r.id} data-testid={`reserva-row-${r.id}`} className="hover:bg-slate-50">
                      <TableCell className="font-mono text-xs text-slate-700">{r.codigo_booking}</TableCell>
                      <TableCell className="text-sm text-slate-800" data-testid={`reserva-cliente-${r.id}`}>
                        {(r.pasajero?.nombre || "—")} {r.pasajero?.apellido || ""}
                      </TableCell>
                      <TableCell className="text-sm font-medium text-slate-900">{nombreTourDisplay(r.tour_nombre, r.modalidad) || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-600">{formatFechaCorta(r.fecha)} · {r.hora}</TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {TIPOS_MODALIDAD[r.modalidad] || r.modalidad}{" "}
                        <span
                          className={`inline-flex items-center justify-center px-1 py-0 ml-1 rounded text-[9px] font-bold border leading-4 ${
                            idiomaAbbr((r.pasajero || {}).idioma) === "EN"
                              ? "bg-blue-50 text-blue-700 border-blue-200"
                              : "bg-slate-50 text-slate-600 border-slate-200"
                          }`}
                        >
                          {idiomaAbbr((r.pasajero || {}).idioma)}
                        </span>
                        {r.variante && VARIANTE_NIAGARA[r.variante] && (
                          <span
                            className={`inline-flex items-center px-1.5 py-0 ml-1 rounded text-[10px] font-medium border ${VARIANTE_NIAGARA[r.variante].cls}`}
                            data-testid={`reserva-variante-${r.id}`}
                          >
                            {VARIANTE_NIAGARA[r.variante].label}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{paxResumen(r)}</TableCell>
                      <TableCell className="text-sm text-slate-700" data-testid={`reserva-valor-${r.id}`}>{money(r.monto_facturado_agencia, r.moneda)}</TableCell>
                      <TableCell className="text-sm text-slate-700" data-testid={`reserva-saldo-${r.id}`}>{money(r.saldo_pendiente, r.moneda)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${est.cls}`} data-testid={`reserva-estado-${r.id}`}>
                          {est.label}
                        </span>
                      </TableCell>
                      <TableCell>
                        {activas(r) ? (
                          <div className="flex items-center gap-2">
                            <EditarPaxDialog reserva={r} onDone={load} />
                            <ReprogramarDialog reserva={r} onDone={load} />
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`cancelar-btn-${r.id}`}>
                                  Cancelar
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>¿Cancelar esta reserva?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Con 24 h o más se cancela y libera el cupo automáticamente. Con menos, queda pendiente de aprobación de Tours NY.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Volver</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => cancelar(r)} data-testid={`cancelar-confirmar-${r.id}`}>
                                    Sí, cancelar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
